import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import toast from 'react-hot-toast';

export interface GradeThresholds {
  APlus: number;
  A: number;
  B: number;
  C: number;
  D: number;
}

export interface HrSettings {
  // Incentive Basics
  defaultIncentivePercent: number;
  financialYearStartMonth: number;
  allowCarryForward: boolean;
  maxCarryForwardMonths: number;

  // Performance — Sales Weights
  targetAchievementWeight: number;
  winRateWeight: number;
  throughputWeight: number;

  // Performance — Engineering Weights
  taskCompletionWeight: number;
  onTimeDeliveryWeight: number;
  milestoneCompletionWeight: number;

  // Performance — Benchmarks & Penalties
  expectedDealsPerPeriod: number;
  warningPenaltyPoints: number;

  // Performance — Grade Bands
  gradeThresholds: GradeThresholds;

  // Track Resolution
  salesDepartments: string[];
  salesRoles: string[];
  engineeringDepartments: string[];
  engineeringRoles: string[];
}

export const DEFAULT_HR_SETTINGS: HrSettings = {
  defaultIncentivePercent: 5,
  financialYearStartMonth: 4,
  allowCarryForward: true,
  maxCarryForwardMonths: 1,

  targetAchievementWeight: 50,
  winRateWeight: 30,
  throughputWeight: 20,

  taskCompletionWeight: 40,
  onTimeDeliveryWeight: 30,
  milestoneCompletionWeight: 30,

  expectedDealsPerPeriod: 5,
  warningPenaltyPoints: 5,

  gradeThresholds: {
    APlus: 90,
    A: 80,
    B: 70,
    C: 60,
    D: 50,
  },

  salesDepartments: ['sales'],
  salesRoles: ['bde', 'bd', 'sales', 'business development'],
  engineeringDepartments: ['it', 'developer', 'development', 'tech', 'engineering'],
  engineeringRoles: ['developer'],
};

export function useHrSettings() {
  return useQuery({
    queryKey: ['hr', 'settings'],
    queryFn: async () => {
      const { data } = await api.get<{ settings: HrSettings }>('/hr-settings');
      return data?.settings ?? DEFAULT_HR_SETTINGS;
    },
    staleTime: 60 * 1000,
  });
}

export function useUpdateHrSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (patch: Partial<HrSettings>) => {
      const { data } = await api.patch<{ settings: HrSettings }>('/hr-settings', patch);
      return data?.settings;
    },
    onSuccess: (newSettings) => {
      toast.success('HR settings saved successfully');
      queryClient.setQueryData(['hr', 'settings'], newSettings);
      queryClient.invalidateQueries({ queryKey: ['hr'] });
    },
    onError: (err: unknown) => {
      toast.error(getApiErrorMessage(err, 'Failed to update HR settings'));
    },
  });
}
